/**
 * 캠페인 수집 기준(`sources`)과 문서에 적힌 글 단위 예산 — 링크 예산(`forcedAdditionBudgets`)·글 예산(`postBudgets`) — 을 읽는 규칙.
 *
 * 키워드·계정은 «브랜드 언급 ∩ 켠 조건»이고 둘 다 켜면 교집합이다. 링크는 그 결과에 더한다.
 * 끈 기준의 값은 저장할 때 비워지므로, 읽는 쪽이 따로 가려야 하는 경우는 «자동 수집 없음» 하나다.
 *
 * ⚠️ `sources` 가 없는 문서(옛 캠페인)는 저장된 값 그대로 동작한다 — 키워드도 계정도 없으면
 *    브랜드 언급 전체를 모은다. 이 문서를 링크 전용으로 읽으면 숫자가 바뀐다.
 */

import type { FsCampaignSources } from 'src/types/firestore-ops/campaign-ops';

import { sanitizeCampaignLink } from './campaign-link-sanitize';
import { matchCampaignLink, campaignLinkIdentity, campaignLinkIdentityIndex } from './campaign-link-identity';
import { getForceAdditionRawLinksFromRow, getForceAdditionOriginalLinksFromRow } from './campaign-forced-addition-links';

/** 저장된 수집 기준. 키가 없거나 모양이 아니면 `null` */
export function readCampaignSources(campaign: any): FsCampaignSources | null {
  const raw = campaign?.sources;
  if (!raw || typeof raw !== 'object') return null;
  return { keyword: raw.keyword === true, account: raw.account === true, link: raw.link === true };
}

/**
 * 자동 수집(브랜드 키워드 ES 조회)을 건너뛰는가 — 키워드·계정을 둘 다 끈 캠페인.
 * ⚠️ `sources` 가 없는 문서는 늘 `false` 다(머리말).
 */
export function isAutoCollectOff(campaign: any): boolean {
  const sources = readCampaignSources(campaign);
  return !!sources && !sources.keyword && !sources.account;
}

/**
 * 편집 화면에 띄울 수집 기준 — 저장값이 있으면 그것, 없으면 채워진 값으로 짐작한다.
 * @returns `unscoped` 는 옛 문서에 키워드도 계정도 없다는 뜻(지금 브랜드 언급 전체를 모은다)
 */
export function inferCampaignSources(campaign: any): { sources: FsCampaignSources; unscoped: boolean } {
  const stored = readCampaignSources(campaign);
  if (stored) return { sources: stored, unscoped: false };
  const keyword = Array.isArray(campaign?.keywords) && campaign.keywords.length > 0;
  const account = Array.isArray(campaign?.accounts) && campaign.accounts.length > 0;
  const link = getForceAdditionOriginalLinksFromRow(campaign).length > 0;
  return { sources: { keyword, account, link }, unscoped: !keyword && !account };
}

/**
 * 지금 등록된 강제추가 링크 — «같은 글»(`campaignLinkIdentity`, 원래 링크에서 센다)마다 처음 저장된 원래 링크 하나.
 * 링크 예산(`readForcedAdditionBudgets`)의 키이고, 표에서 고칠 때 맞추는 등록 링크다(`matchCampaignLink` 에 넘긴다).
 */
export function readForcedAdditionLiveLinks(campaign: any): ReadonlySet<string> {
  const seen = new Set<string>();
  const out = new Set<string>();
  getForceAdditionRawLinksFromRow(campaign).forEach((link) => {
    const identity = campaignLinkIdentity(link);
    if (!identity || seen.has(identity)) return;
    seen.add(identity);
    out.add(link);
  });
  return out;
}

/**
 * 링크 → 예산. 지금 등록된 강제추가 링크의 예산만 담는다(지운 링크의 예산은 세지 않는다).
 * 키는 `readForcedAdditionLiveLinks` 의 원래 링크다 — 글에 붙일 때는 `matchCampaignLink` 로 맞춘다(색인 글의 링크는 모양이 다르다).
 * `forcedAdditionBudgets` 의 줄은 원래 링크의 «같은 글» 열쇠로 등록 링크에 붙고, 같은 글의 줄이 여럿이면 뒤엣것이 이긴다
 * (서버 저장 `toLinkBudgets` 와 같은 차례). 0 이하는 뺀다.
 */
export function readForcedAdditionBudgets(campaign: any): Map<string, number> {
  const live = campaignLinkIdentityIndex(readForcedAdditionLiveLinks(campaign));
  const byIdentity = new Map<string, number>();
  (Array.isArray(campaign?.forcedAdditionBudgets) ? campaign.forcedAdditionBudgets : []).forEach((item: any) => {
    const identity = campaignLinkIdentity(item?.link);
    if (identity && live.has(identity)) byIdentity.set(identity, Number(item?.budget) || 0);
  });
  const map = new Map<string, number>();
  live.forEach((link, identity) => {
    const budget = byIdentity.get(identity) ?? 0;
    if (budget > 0) map.set(link, budget);
  });
  return map;
}

/** 링크 예산의 합 — 캠페인 총 예산에 더해진다 */
export function sumForcedAdditionBudgets(campaign: any): number {
  let sum = 0;
  readForcedAdditionBudgets(campaign).forEach((budget) => { sum += budget; });
  return sum;
}

/** 글 예산 맵(`postBudgets`)의 키 앞말 — 키는 `link:` + 글의 «같은 글» 열쇠(`campaignPostBudgetIdentity`) */
export const CAMPAIGN_POST_BUDGET_KEY_PREFIX = 'link:';

/**
 * 글 예산의 «같은 글» 열쇠 — `campaignLinkIdentity`(원래 링크에서), 그것이 비면 정규화 링크.
 * 그래서 쇼츠로 본 글과 `watch?v=` 로 색인된 같은 글이 한 칸이고, 다른 영상의 `watch?v=` 는 다른 칸이다.
 * @returns 링크가 비거나 `N/A` 면 빈 문자열
 */
export function campaignPostBudgetIdentity(link: unknown): string {
  const raw = String(link ?? '').trim();
  if (!raw || raw === 'N/A') return '';
  return campaignLinkIdentity(raw) || sanitizeCampaignLink(raw);
}

/**
 * 글 예산 맵의 키 — `link:` + `campaignPostBudgetIdentity`.
 * @returns 링크가 비면 빈 문자열
 */
export function campaignPostBudgetKey(link: unknown): string {
  const identity = campaignPostBudgetIdentity(link);
  return identity ? `${CAMPAIGN_POST_BUDGET_KEY_PREFIX}${identity}` : '';
}

/**
 * 글들의 글 예산 키(중복 없음 · 링크 없는 글은 뺀다) — 글을 제외할 때 그 글의 `postBudgets` 키를 함께 지우는 데 쓴다.
 * @param posts 글(`link` · `url`)
 */
export function campaignPostBudgetKeysOfPosts(posts: readonly unknown[] | null | undefined): string[] {
  const out = new Set<string>();
  (Array.isArray(posts) ? posts : []).forEach((post) => {
    const row = post as { link?: unknown; url?: unknown } | null | undefined;
    const key = campaignPostBudgetKey(row?.link || row?.url || '');
    if (key) out.add(key);
  });
  return [...out];
}

/**
 * «같은 글» 열쇠 → 글 예산(`postBudgets` — 강제추가 링크가 아닌 글에 포스팅 표에서 넣은 예산). 0 이하·숫자 아닌 값·`link:` 아닌 키는 뺀다.
 * 🔑 같은 글(`campaignLinkIdentity`)에 링크 예산(`readForcedAdditionBudgets`)이 있으면 링크 예산이 이기고 글 예산은 뺀다 — 한 글의 예산은 한 번만 센다.
 * ⚠️ 지금 보이지 않는 글(제외 · 기간 밖)의 키도 센다 — 제외한 글의 키는 제외할 때 함께 지운다(`campaignPostBudgetKeysOfPosts`).
 */
export function readCampaignPostBudgets(campaign: any): Map<string, number> {
  const raw = campaign?.postBudgets;
  const map = new Map<string, number>();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return map;
  const linkBudgetIdentities = campaignLinkIdentityIndex(readForcedAdditionBudgets(campaign));
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    if (!key.startsWith(CAMPAIGN_POST_BUDGET_KEY_PREFIX)) return;
    const identity = key.slice(CAMPAIGN_POST_BUDGET_KEY_PREFIX.length).trim();
    const budget = Number(value);
    if (!identity || !Number.isFinite(budget) || budget <= 0 || linkBudgetIdentities.has(identity)) return;
    map.set(identity, budget);
  });
  return map;
}

/** 글 예산의 합 — 캠페인 총 예산에 더해진다 */
export function sumCampaignPostBudgets(campaign: any): number {
  let sum = 0;
  readCampaignPostBudgets(campaign).forEach((budget) => { sum += budget; });
  return sum;
}

/**
 * 글 하나에 넣은 예산 — 링크 예산(`matchCampaignLink` 로 같은 글을 찾는다) → 글 예산(`campaignPostBudgetIdentity` 로 찾는다) 차례.
 * @param linkBudgets `readForcedAdditionBudgets` 결과
 * @param postBudgets `readCampaignPostBudgets` 결과
 * @returns 없으면 `null` · 있으면 예산과 «같은 글» 열쇠(같은 열쇠의 둘째 글은 다시 받지 않는다)
 */
export function campaignOwnPostBudgetOf(
  post: any,
  linkBudgets: ReadonlyMap<string, number>,
  postBudgets: ReadonlyMap<string, number>,
): { budget: number; key: string } | null {
  const link = matchCampaignLink(post, linkBudgets);
  if (link) return { budget: linkBudgets.get(link) ?? 0, key: `forced:${link}` };
  const own = campaignPostBudgetIdentity(post?.link || post?.url || '');
  if (own && postBudgets.has(own)) return { budget: postBudgets.get(own) ?? 0, key: `post:${own}` };
  return null;
}
